export const DEPLOYMENT_ID_HEADER = 'x-deployment-id';

type PageLocation = Pick<Location, 'href' | 'origin'>;

/**
 * Decorates `fetch` so that same-origin requests carry the deployment id header.
 *
 * The header is only safe to add on same-origin requests.
 * A custom request header makes any cross-origin request non-simple, so the browser sends a CORS preflight and fails
 * the request unless the target allows the header.
 * The entire Origin, not just hostname, needs to be verified: the same host on another port or scheme (http vs https) is cross-origin.
 */
export function withDeploymentIdHeader(
    originalFetch: typeof fetch,
    deploymentId: string,
    pageLocation: PageLocation,
): typeof fetch {
    return (input, init) => {
        if (!isSameOrigin(input, pageLocation)) {
            return originalFetch(input, init);
        }
        const headers = resolveHeaders(input, init);
        headers.set(DEPLOYMENT_ID_HEADER, deploymentId);
        return originalFetch(input, { ...init, headers });
    };
}

function isSameOrigin(input: RequestInfo | URL, pageLocation: PageLocation): boolean {
    const requestUrl = parseRequestUrl(input, pageLocation.href);
    return requestUrl?.origin === pageLocation.origin;
}

function parseRequestUrl(input: RequestInfo | URL, baseHref: string): URL | null {
    const rawUrl = input instanceof Request ? input.url : input.toString();
    try {
        return new URL(rawUrl, baseHref);
    } catch {
        // Let the original fetch produce its own TypeError for a malformed URL.
        return null;
    }
}

function resolveHeaders(input: RequestInfo | URL, init: RequestInit | undefined): Headers {
    if (init?.headers !== undefined) {
        return new Headers(init.headers);
    }
    if (input instanceof Request) {
        return new Headers(input.headers);
    }
    return new Headers();
}
